import { useEffect } from 'react';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';

/**
 * Hook to enable Enter key navigation between form inputs
 *
 * When a user presses Enter on an input/textarea field, focus moves to the next
 * focusable input instead of submitting the form.
 *
 * Can be disabled via useGlobalBehavior().setEnterKeyNavigationEnabled(false)
 *
 * Usage:
 * - Add this hook to your root layout or main app component
 * - It will automatically work for all input/textarea elements
 *
 * @example
 * function MyComponent() {
 *   useEnterKeyNavigation();
 *   return <YourApp />;
 * }
 */
export const useEnterKeyNavigation = () => {
  const { enterKeyNavigationEnabled } = useGlobalBehavior();

  useEffect(() => {
    // Skip if behavior is disabled
    if (!enterKeyNavigationEnabled) {
      return;
    }

    const handleEnterKey = (event: KeyboardEvent) => {
      // Check if the target is an input or textarea
      const target = event.target as HTMLElement;
      const isInputElement =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

      // Only handle Enter key
      if (event.key !== 'Enter' || !isInputElement) {
        return;
      }

      // Don't prevent default for special cases:
      // - Buttons (should submit/trigger on Enter)
      // - Elements with Enter key handlers
      // - Ctrl/Cmd + Enter (often used for form submission)
      if (event.ctrlKey || event.metaKey) {
        return;
      }

      // For textareas, allow normal Enter key for new lines
      if (target instanceof HTMLTextAreaElement) {
        return;
      }

      // Prevent default form submission
      event.preventDefault();

      // Get all focusable elements on the page
      const focusableElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          'input, textarea, button, select, a[href], [tabindex]:not([tabindex="-1"])'
        )
      );

      // Filter to only visible, enabled elements
      const visibleFocusableElements = focusableElements.filter(el => {
        // Skip hidden elements
        if (el.offsetParent === null) {
          return false;
        }
        // Skip disabled inputs
        if (
          (el instanceof HTMLInputElement ||
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLButtonElement ||
            el instanceof HTMLSelectElement) &&
          el.disabled
        ) {
          return false;
        }
        // Opt-out: elements (or any ancestor) marked data-skip-enter-nav are
        // removed from the Enter-key sequence. Tab/Shift+Tab still reach them.
        if (el.closest('[data-skip-enter-nav]')) {
          return false;
        }
        return true;
      });

      // Find the current element's index
      const currentIndex = visibleFocusableElements.indexOf(target);

      // Move to the next focusable element
      if (currentIndex !== -1 && currentIndex < visibleFocusableElements.length - 1) {
        const nextElement = visibleFocusableElements[currentIndex + 1];
        nextElement.focus();
      }
    };

    // Attach event listener to the document
    document.addEventListener('keydown', handleEnterKey);

    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [enterKeyNavigationEnabled]);
};
